import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';

/**
 * SidebarMoreMenu — "More" pill at the bottom of the workspace rail.
 *
 * Click → opens an upward-flying popover with the secondary nav items
 * that don't justify a top-level sidebar row: Help, Pricing, Settings,
 * and the full legal pages strip. New rows just plug in at the right
 * section in the MENU array below.
 *
 * Outside-click and Escape close the popover. Each row that has an
 * `href` opens in a new tab (used for the legal pages which live as
 * static HTML in /public). Rows with `view` route through onNavigate.
 *
 * The "Admin" section is conditionally injected at render time for
 * users with CMS access (admin OR scoped manager). This is the
 * canonical in-app entrance to /admin.
 */

const MENU = [
  {
    // The flattened Home/Create/Collaborate/Grow/Monetize rail (Home
    // Redesign) doesn't have room for every destination as a top-level
    // row — Library, Clips, and Matches live here instead.
    section: 'Workspace',
    rows: [
      { id: 'library', label: 'Library', view: 'episodes' },
      { id: 'clips',   label: 'Clips',   view: 'clips'    },
      { id: 'matches', label: 'Matches', view: 'matches'  },
    ],
  },
  {
    section: 'App',
    rows: [
      // Help moved to a dedicated button at the bottom of the rail
      // (under the credits block) — it's one click instead of buried
      // in this popover.
      { id: 'pricing',  label: 'Pricing',  view: 'pricing'  },
      { id: 'settings', label: 'Settings', view: 'settings' },
    ],
  },
  {
    section: 'Legal',
    rows: [
      { id: 'guidelines', label: 'Community Guidelines', href: '/community-guidelines.html' },
      { id: 'privacy',    label: 'Privacy Policy',   href: '/privacy.html'           },
      { id: 'terms',      label: 'Terms of Use',     href: '/terms.html'             },
      { id: 'disclaimer', label: 'Disclaimer',       href: '/disclaimer.html'        },
      { id: 'cookies',    label: 'Cookie Policy',    href: '/cookie-policy.html'     },
      { id: 'aup',        label: 'Acceptable Use',   href: '/acceptable-use.html'    },
      { id: 'dmca',       label: 'DMCA',             href: '/dmca.html'              },
      { id: 'ai-policy',  label: 'AI Policy',        href: '/ai-content-policy.html' },
    ],
  },
];

export default function SidebarMoreMenu({ onNavigate, collapsed = false }) {
  const { hasAnyCmsAccess, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Build the menu with an Admin section prepended for CMS users.
  // Admins see "Admin (full access)" label; scoped managers see
  // "Admin (manager)" so it's obvious which seat they're in.
  const menu = hasAnyCmsAccess
    ? [
        {
          section: isAdmin ? 'Admin' : 'Manager',
          rows: [
            { id: 'admin', label: isAdmin ? 'Open admin' : 'Open dashboard', view: 'admin' },
          ],
        },
        ...MENU,
      ]
    : MENU;

  // Outside-click + Escape to close. Only attaches listeners while open.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleRowClick(row) {
    setOpen(false);
    if (row.href) {
      try { window.open(row.href, '_blank', 'noopener,noreferrer'); } catch { window.location.href = row.href; }
      return;
    }
    if (row.view) onNavigate?.(row.view);
  }

  return (
    <div className="ws-more-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`ws-nav-item${open ? ' ws-nav-item-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={collapsed ? 'More' : undefined}
      >
        <span className="ws-nav-icon" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5"  cy="12" r="1.8"/>
            <circle cx="12" cy="12" r="1.8"/>
            <circle cx="19" cy="12" r="1.8"/>
          </svg>
        </span>
        {!collapsed && <span className="ws-nav-label">More</span>}
      </button>

      {open && (
        <div className="ws-more-menu" role="menu">
          {menu.map((section, sIdx) => (
            <div key={section.section} className="ws-more-menu-section">
              {sIdx > 0 && <div className="ws-more-menu-sep" aria-hidden />}
              <div className="ws-more-menu-label">{section.section}</div>
              {section.rows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className="ws-more-menu-row"
                  role="menuitem"
                  onClick={() => handleRowClick(row)}
                >
                  <span>{row.label}</span>
                  {row.href && (
                    /* External-link glyph for legal pages that open in
                       a new tab — tells the user this leaves the app. */
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
